// src/components/AdminSitesMap.jsx
//
// Fleet-wide map: one marker per site that has a known location.
// Location comes from either device type - Peplink GPS is preferred,
// Starlink is the fallback when the site's Peplink(s) have no fix (see
// sites_map() docstring in service.py). Each site's marker/popup shows
// which one it actually came from via location_source.
//
// Important map behavior:
// - Site coordinates are plotted directly from location.lat / location.lng.
// - Coordinates are validated and longitude is normalized to [-180, 180].
// - No clustering, jittering, spiderfying, or coordinate offsets are used.
// - World wrapping is disabled so the same site cannot appear in another
//   horizontal copy of the world when zoomed out.
// - Nearby sites are intentionally allowed to overlap when zoomed out.

import React, { useEffect, useState, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';
import { apiFetchJSON } from '../api/apiClient';
import FleetHeader from './FleetHeader';
import { usePageTour } from './tour/tourStore';
import '../styles/fleet-theme.css';
import './AdminSitesMap.css';

const PAGE_SIZE = 12;

const WORLD_LAT_MIN = -85.05112878;
const WORLD_LAT_MAX = 85.05112878;
const WORLD_LNG_MIN = -180;
const WORLD_LNG_MAX = 180;

const STATE_COLOR = {
  online: '#88F4EB',
  attention: '#CAFB6D',
  down: '#fff',
  unknown: 'rgba(255,255,255,.35)',
};

// Small badge shown on the marker and in the popup so it's obvious at
// a glance which device type a site's plotted position came from.
const SOURCE_LABEL = {
  peplink: 'Peplink',
  starlink: 'Starlink',
};

const SOURCE_BADGE_LETTER = {
  peplink: 'P',
  starlink: 'S',
};

function siteState(devices) {
  if (!devices || devices.length === 0) return 'unknown';
  const offlineCount = devices.filter((d) => d.online === false).length;
  if (offlineCount === devices.length) return 'down';
  if (offlineCount > 0) return 'attention';
  return 'online';
}

function siteBreakdown(devices) {
  const list = devices || [];
  return {
    peplink: list.filter((d) => d.device_type === 'peplink').length,
    starlinkMaritime: list.filter(
      (d) => d.device_type === 'starlink' && d.service !== 'roam'
    ).length,
    starlinkRoam: list.filter(
      (d) => d.device_type === 'starlink' && d.service === 'roam'
    ).length,
  };
}

/**
 * Convert API location values into a safe Leaflet LatLng.
 *
 * We intentionally do NOT swap lat/lng automatically. If the backend sends
 * them in the wrong order, silently swapping them would hide a data problem.
 * Longitude is normalized because values such as 280° and -80° represent the
 * same geographic longitude.
 */
function normalizeLocation(location) {
  if (!location || typeof location !== 'object') return null;

  const lat = Number(location.lat);
  const rawLng = Number(location.lng);

  if (!Number.isFinite(lat) || !Number.isFinite(rawLng)) return null;
  if (lat < -90 || lat > 90) return null;

  // Normalize longitude without changing the geographic position.
  const lng = ((rawLng + 180) % 360 + 360) % 360 - 180;

  return { lat, lng };
}

/**
 * Escape values inserted into popup HTML.
 * Site names come from the API, so don't insert them as raw HTML.
 */
function escapeHtml(value) {
  return String(value ?? '')
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#039;');
}

/**
 * True if a site matches the search box. Matches on site name and on
 * the serial of whichever device supplied its plotted location (so
 * searching a Peplink serial or Starlink terminal id also works) -
 * source_device_key is already returned by /admin/sites-map, so this
 * needs no extra request.
 */
function siteMatchesQuery(site, normalizedQuery) {
  if (!normalizedQuery) return true;
  const haystack = `${site.site_name || ''} ${site.source_device_key || ''}`.toLowerCase();
  return haystack.includes(normalizedQuery);
}

/**
 * Formats an ISO timestamp (UTC, as returned by /admin/sites-map) as a
 * short Europe/Paris date+time, e.g. "18 Sep, 09:09". Europe/Paris is
 * used as a fixed fleet-wide reference regardless of where the site
 * actually is or what timezone its own devices report in - see
 * sites_map()'s DISPLAY_TZ in service.py.
 */
function formatParisTime(isoString) {
  if (!isoString) return null;
  try {
    const date = new Date(isoString);
    if (Number.isNaN(date.getTime())) return null;
    return new Intl.DateTimeFormat('en-GB', {
      timeZone: 'Europe/Paris',
      day: '2-digit',
      month: 'short',
      hour: '2-digit',
      minute: '2-digit',
    }).format(date);
  } catch {
    return null;
  }
}

/**
 * One-line description of where a site's plotted position came from,
 * shared between the marker's hover title and the popup body so they
 * never say different things. Both sources now carry a
 * location_timestamp and a location_stale flag (backend: Peplink's own
 * location_timestamp/location_map point, or Starlink's
 * online_synced_at) - so this no longer special-cases Peplink.
 */
function locationSourceText(site) {
  const label = SOURCE_LABEL[site.location_source];
  if (!label) return null;
  if (!site.location_timestamp) {
    return `Location from ${label}`;
  }
  const parisTime = formatParisTime(site.location_timestamp);
  const timePart = parisTime ? ` · ${parisTime} Paris` : '';
  const stalePart = site.location_stale ? ' (stale)' : '';
  return `Location from ${label}${timePart}${stalePart}`;
}

export default function AdminSitesMap({ user, onLogout }) {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [sites, setSites] = useState([]);
  const [siteDetail, setSiteDetail] = useState({});
  const [statusLoading, setStatusLoading] = useState(true);
  const [activeFilter, setActiveFilter] = useState('all');
  const [searchQuery, setSearchQuery] = useState('');
  const geocodeCache = useRef({});

  const mapRef = useRef(null);
  const mapInstance = useRef(null);
  const markersRef = useRef([]);

  useEffect(() => {
    let cancelled = false;

    async function load() {
      setLoading(true);
      setError(null);

      try {
        const json = await apiFetchJSON('/api/v2/admin/sites-map');
        if (!cancelled) setSites(json.sites || []);
      } catch (e) {
        if (!cancelled) {
          setError(e.message || 'Failed to load site locations');
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    }

    load();
    return () => {
      cancelled = true;
    };
  }, []);

  // Cross-reference with the paginated per-device data to get real status.
  useEffect(() => {
    let cancelled = false;

    async function loadStatus() {
      setStatusLoading(true);

      try {
        let offset = 0;
        let hasMore = true;
        const map = {};

        while (hasMore && !cancelled) {
          const page = await apiFetchJSON(
            `/api/v2/admin/sites-page?offset=${offset}&limit=${PAGE_SIZE}`
          );

          (page.sites || []).forEach((s) => {
            map[s.site_id] = {
              state: siteState(s.devices),
              ...siteBreakdown(s.devices),
            };
          });

          if (!cancelled) setSiteDetail({ ...map });

          offset += (page.sites || []).length;
          hasMore = page.has_more;
        }
      } catch {
        // Non-fatal. Markers remain neutral if status loading fails.
      } finally {
        if (!cancelled) setStatusLoading(false);
      }
    }

    loadStatus();
    return () => {
      cancelled = true;
    };
  }, []);

  // Validate coordinates once. Invalid/missing locations stay in the sidebar
  // instead of producing broken markers or misleading positions.
  const locatedSites = sites.filter((s) => normalizeLocation(s.location));
  const unlocatedSites = sites.filter((s) => !normalizeLocation(s.location));

  const normalizedQuery = searchQuery.trim().toLowerCase();

  const visibleSites = locatedSites.filter((s) => {
    if (activeFilter !== 'all' && siteDetail[s.site_id]?.state !== activeFilter) {
      return false;
    }
    return siteMatchesQuery(s, normalizedQuery);
  });

  // The sidebar list narrows with the same search box, so a searched-for
  // site that has no GPS fix at all still turns up (with an explanation)
  // instead of just vanishing.
  const visibleUnlocatedSites = unlocatedSites.filter((s) =>
    siteMatchesQuery(s, normalizedQuery)
  );

  const counts = {
    online: locatedSites.filter(
      (s) => siteDetail[s.site_id]?.state === 'online'
    ).length,
    attention: locatedSites.filter(
      (s) => siteDetail[s.site_id]?.state === 'attention'
    ).length,
    down: locatedSites.filter(
      (s) => siteDetail[s.site_id]?.state === 'down'
    ).length,
  };

  const resolveLocationLabel = useCallback(async (site) => {
    if (geocodeCache.current[site.site_id]) {
      return geocodeCache.current[site.site_id];
    }

    const location = normalizeLocation(site.location);
    if (!location) return null;

    try {
      const res = await fetch(
        `https://nominatim.openstreetmap.org/reverse?format=jsonv2&lat=${location.lat}&lon=${location.lng}&zoom=10`,
        { headers: { Accept: 'application/json' } }
      );

      const data = await res.json();
      const addr = data?.address || {};
      const place =
        addr.city || addr.town || addr.village || addr.county || addr.state;

      const label = place
        ? `Near ${place}${addr.country_code ? ', ' + addr.country_code.toUpperCase() : ''}`
        : 'At sea';

      geocodeCache.current[site.site_id] = label;
      return label;
    } catch {
      return null;
    }
  }, []);

  const buildPopupHtml = useCallback(
    (s, locationLabel) => {
      const detail = siteDetail[s.site_id];
      const state = detail?.state || 'unknown';
      const label =
        state === 'down'
          ? 'DOWN'
          : state === 'attention'
            ? 'ATTENTION'
            : state === 'online'
              ? 'ONLINE'
              : '—';
      const color = STATE_COLOR[state];
      const location = normalizeLocation(s.location);
      const parts = [];

      if (detail) {
        if (detail.peplink > 0) parts.push(`${detail.peplink} Peplink`);
        if (detail.starlinkMaritime > 0) {
          parts.push(`${detail.starlinkMaritime} Starlink Maritime`);
        }
        if (detail.starlinkRoam > 0) {
          parts.push(`${detail.starlinkRoam} Starlink Roam`);
        }
      }

      const deviceLine =
        parts.length > 0
          ? parts.join(' · ')
          : `${s.device_count} device${s.device_count !== 1 ? 's' : ''}`;

      const coordinateLine = location
        ? `${location.lat.toFixed(6)} · ${location.lng.toFixed(6)}`
        : 'Invalid coordinates';

      const sourceText = locationSourceText(s);

      return `
        <div class="asm-popup">
          <div class="asm-popup-top">
            <span class="asm-popup-name">${escapeHtml(s.site_name)}</span>
            <span class="asm-popup-badge" style="background:${color}22;color:${color}">${label}</span>
          </div>
          <div class="asm-popup-meta">${escapeHtml(locationLabel || coordinateLine)}</div>
          ${
            sourceText
              ? `<div class="asm-popup-meta asm-popup-source"${
                  s.location_stale ? ' style="color:#e8b04b"' : ''
                }>${escapeHtml(sourceText)}</div>`
              : ''
          }
          <div class="asm-popup-meta">${escapeHtml(deviceLine)}</div>
          <div class="asm-popup-link" data-site-id="${escapeHtml(s.site_id)}">OPEN SITE →</div>
        </div>
      `;
    },
    [siteDetail]
  );

  // Create the map once and deliberately disable world wrapping.
  useEffect(() => {
    if (!mapRef.current || loading || mapInstance.current) return;

    const map = L.map(mapRef.current, {
      zoomControl: false,
      crs: L.CRS.EPSG3857,
      minZoom: 2,
      maxZoom: 19,
      worldCopyJump: false,
      maxBounds: [
        [WORLD_LAT_MIN, WORLD_LNG_MIN],
        [WORLD_LAT_MAX, WORLD_LNG_MAX],
      ],
      maxBoundsViscosity: 1.0,
    });

    L.tileLayer('https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png', {
      attribution: '&copy; OpenStreetMap contributors',
      className: 'asm-dark-tiles',
      maxZoom: 19,
      noWrap: true,
    }).addTo(map);

    mapInstance.current = map;

    // The map container can be laid out after the component mounts.
    // invalidateSize makes Leaflet recalculate the pixel origin correctly.
    requestAnimationFrame(() => map.invalidateSize());
  }, [loading]);

  // Draw markers. This effect never changes a site's coordinates based on zoom.
  useEffect(() => {
    const map = mapInstance.current;
    if (!map || loading) return;

    markersRef.current.forEach((marker) => map.removeLayer(marker));
    markersRef.current = [];

    if (visibleSites.length === 0) return;

    const bounds = [];
    let soleMarker = null;

    visibleSites.forEach((s) => {
      const location = normalizeLocation(s.location);
      if (!location) return;

      const { lat, lng } = location;
      bounds.push([lat, lng]);

      const state = siteDetail[s.site_id]?.state || 'unknown';
      const color = STATE_COLOR[state];
      const sourceLetter = SOURCE_BADGE_LETTER[s.location_source] || '';
      const sourceTitle = locationSourceText(s) || '';
      const badgeBorder = s.location_stale
        ? `1px dashed ${color}99`
        : `1px solid ${color}55`;

      // Use a unique marker class and inline root styling. This avoids any
      // project CSS accidentally overriding Leaflet's marker transform.
      const icon = L.divIcon({
        className: 'asm-marker-icon',
        html: `
          <span
            style="
              position:absolute;
              left:50%;
              top:50%;
              width:20px;
              height:20px;
              transform:translate(-50%,-50%);
              border-radius:50%;
              background:${color}24;
            "
          ></span>
          <span
            style="
              position:absolute;
              left:50%;
              top:50%;
              width:8px;
              height:8px;
              transform:translate(-50%,-50%);
              border-radius:50%;
              background:${color};
              box-shadow:0 0 8px ${color}99;
            "
          ></span>
          ${
            sourceLetter
              ? `<span
                  title="${escapeHtml(sourceTitle)}"
                  style="
                    position:absolute;
                    left:50%;
                    top:50%;
                    transform:translate(2px,-14px);
                    font-size:9px;
                    font-weight:700;
                    line-height:1;
                    padding:1px 3px;
                    border-radius:3px;
                    background:rgba(10,14,20,.85);
                    color:${color};
                    border:${badgeBorder};
                  "
                >${sourceLetter}</span>`
              : ''
          }
        `,
        iconSize: [20, 20],
        iconAnchor: [10, 10],
      });

      const marker = L.marker([lat, lng], {
        icon,
        // Explicitly keep the marker in the normal Leaflet marker pane.
        pane: 'markerPane',
        interactive: true,
      })
        .addTo(map)
        .bindPopup(buildPopupHtml(s, null), {
          className: 'asm-popup-wrap',
          closeButton: false,
        });

      marker.on('popupopen', (e) => {
        const wireOpenLink = () => {
          const link = e.popup.getElement()?.querySelector('.asm-popup-link');
          if (link) {
            link.onclick = () =>
              navigate(`/admin/dashboard?site=${s.site_id}`);
          }
        };

        wireOpenLink();

        resolveLocationLabel(s).then((label) => {
          if (label && mapInstance.current) {
            e.popup.setContent(buildPopupHtml(s, label));
            wireOpenLink();
          }
        });
      });

      markersRef.current.push(marker);
      if (visibleSites.length === 1) soleMarker = marker;
    });

    if (bounds.length === 1) {
      // One site: start close enough to make the exact location useful.
      map.setView(bounds[0], 8);
      // If that single marker is the result of a search (not just the
      // fleet happening to have one located site), open its popup right
      // away - that's the whole point of searching instead of clicking.
      if (normalizedQuery && soleMarker) {
        soleMarker.openPopup();
      }
    } else if (bounds.length > 1) {
      // Fit the fleet, but never zoom farther out than level 2.
      // This avoids an unnecessarily tiny world map while preserving the
      // actual latitude/longitude of every marker.
      map.fitBounds(L.latLngBounds(bounds), {
        padding: [40, 40],
        maxZoom: 5,
      });
    }
  }, [
    visibleSites,
    siteDetail,
    loading,
    buildPopupHtml,
    resolveLocationLabel,
    navigate,
    normalizedQuery,
  ]);

  useEffect(
    () => () => {
      if (mapInstance.current) {
        mapInstance.current.remove();
        mapInstance.current = null;
      }
    },
    []
  );

  // ---- Guided tour ("?" in FleetHeader) ---------------------------------
  usePageTour('sites-map', {
    siteCount: sites.length,
    placedCount: locatedSites.length,
    unplacedCount: unlocatedSites.length,
    counts,
    statusLoading,
    onTourStart: () => mapInstance.current?.closePopup(),
  }, { ready: !loading && !error });

  return (
    <div>
      <FleetHeader title="Sites Map" user={user} onLogout={onLogout} />

      <div className="asm-page">
        {loading && <div className="asm-loading">Loading site locations…</div>}
        {error && <div className="ft-toolbar-error" role="alert"><i className="fas fa-exclamation-triangle me-2"></i>{error}</div>}

        {!loading && !error && (
          <div className="asm-grid">
            <div className="asm-map-col">
              <div className="asm-toolbar" data-tour="map-toolbar">
                <span className="asm-eyebrow">PLACED</span>
                <span className="asm-placed-count">
                  {locatedSites.length} OF {sites.length}
                </span>

                <div
                  className="asm-search"
                  style={{
                    position: 'relative',
                    flex: '0 1 220px',
                    margin: '0 12px',
                  }}
                >
                  <input
                    type="text"
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    placeholder="Search site name"
                    aria-label="Search sites"
                    style={{
                      width: '100%',
                      boxSizing: 'border-box',
                      padding: '6px 26px 6px 10px',
                      borderRadius: 6,
                      border: '1px solid rgba(255,255,255,.18)',
                      background: 'rgba(255,255,255,.06)',
                      color: '#fff',
                      fontSize: 13,
                      outline: 'none',
                    }}
                  />
                  {searchQuery && (
                    <button
                      type="button"
                      onClick={() => setSearchQuery('')}
                      aria-label="Clear search"
                      style={{
                        position: 'absolute',
                        right: 6,
                        top: '50%',
                        transform: 'translateY(-50%)',
                        background: 'transparent',
                        border: 'none',
                        color: 'rgba(255,255,255,.6)',
                        cursor: 'pointer',
                        fontSize: 15,
                        lineHeight: 1,
                        padding: 2,
                      }}
                    >
                      ×
                    </button>
                  )}
                </div>

                <div className="asm-pill-row">
                  <button
                    className={'asm-pill' + (activeFilter === 'all' ? ' active' : '')}
                    onClick={() => setActiveFilter('all')}
                  >
                    ALL
                  </button>
                  <button
                    className={
                      'asm-pill' +
                      (activeFilter === 'attention' ? ' active' : '')
                    }
                    onClick={() => setActiveFilter('attention')}
                  >
                    ATTENTION {counts.attention}
                  </button>
                  <button
                    className={
                      'asm-pill' + (activeFilter === 'down' ? ' active' : '')
                    }
                    onClick={() => setActiveFilter('down')}
                  >
                    DOWN {counts.down}
                  </button>
                </div>
              </div>

              <div className="asm-map-wrap" data-tour="map-map">
                <div ref={mapRef} className="asm-map" />

                {visibleSites.length === 0 && (
                  <div className="asm-map-empty">
                    {locatedSites.length === 0
                      ? 'No sites with a known location yet.'
                      : searchQuery
                        ? `No located sites match "${searchQuery}".`
                        : 'No located sites match this filter.'}
                  </div>
                )}

                <div className="asm-zoom-controls">
                  <button
                    onClick={() => mapInstance.current?.zoomIn()}
                    aria-label="Zoom in"
                  >
                    +
                  </button>
                  <button
                    onClick={() => mapInstance.current?.zoomOut()}
                    aria-label="Zoom out"
                  >
                    −
                  </button>
                </div>

                <div className="asm-legend" data-tour="map-legend">
                  <span>
                    <span
                      className="asm-legend-dot"
                      style={{ background: STATE_COLOR.online }}
                    />
                    ONLINE {counts.online}
                  </span>
                  <span>
                    <span
                      className="asm-legend-dot"
                      style={{ background: STATE_COLOR.attention }}
                    />
                    ATTENTION {counts.attention}
                  </span>
                  <span>
                    <span
                      className="asm-legend-dot"
                      style={{ background: STATE_COLOR.down }}
                    />
                    DOWN {counts.down}
                  </span>
                </div>

                {statusLoading && (
                  <div className="asm-status-loading">Loading status…</div>
                )}
              </div>
            </div>

            <div className="asm-sidebar" data-tour="map-unplaced">
              <div className="asm-sidebar-head">
                <span className="asm-eyebrow accent">NOT PLACED</span>
                <span className="asm-count-badge">{unlocatedSites.length}</span>
              </div>

              {unlocatedSites.length === 0 ? (
                <div className="asm-sidebar-empty">
                  Every site has a known location.
                </div>
              ) : visibleUnlocatedSites.length === 0 ? (
                <div className="asm-sidebar-empty">
                  No unassigned sites match "{searchQuery}".
                </div>
              ) : (
                <>
                  <div className="asm-sidebar-note">
                    Position comes from the site&apos;s Peplink GPS, or its
                    Starlink if the Peplink doesn&apos;t report a location.
                    These sites have neither yet — assign a device with a
                    GPS fix to the site in Site Manager to place it here.
                  </div>

                  <div className="asm-unlocated-list">
                    {visibleUnlocatedSites.map((s) => (
                      <div
                        key={s.site_id}
                        className="asm-unlocated-row"
                        onClick={() => navigate('/admin/site-manager')}
                      >
                        <span className="asm-unlocated-name">
                          {s.site_name}
                        </span>
                        <span className="asm-unlocated-action">ASSIGN →</span>
                      </div>
                    ))}
                  </div>
                </>
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
